import { QueryClient } from '@tanstack/react-query';
import { userManager } from '@src/auth/oidcConfig';
import { Paths } from '@src/routes/paths';

// Parallel queries can all fail at once, and each one lands here. Without this
// guard every one of them would clear the session and start its own navigation.
let sessionTeardownStarted = false;

/**
 * Called once a request has already been renewed and retried by the auth
 * middleware and still came back 401, meaning the stored session is dead.
 *
 * Only the local session is dropped, deliberately not the Keycloak SSO session:
 * if that is still valid, /login re-authenticates without a visible prompt. The
 * previous implementation called signoutSilent() as a floating promise, which
 * needs an iframe and a post_logout_redirect_uri and so rejected unhandled while
 * the redirect below tore the page down anyway.
 */
const clearDeadSession = (): void => {
  if (sessionTeardownStarted) {
    return;
  }
  sessionTeardownStarted = true;
  userManager
    .removeUser()
    .catch((error: unknown) => {
      console.warn('OIDC: failed to clear stored session', error);
    })
    .finally(() => {
      window.location.href = Paths.LOGIN;
    });
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      throwOnError: (error) => {
        console.error(error);
        if ('statusCode' in error) {
          if (error.statusCode === 401) {
            clearDeadSession();
          } else if (error.statusCode === 403) {
            // Authenticated but not permitted. Keep the session; clearing it
            // would silently re-authenticate and hit the same 403 in a loop.
            window.location.href = Paths.LOGIN;
          }
        }
        return false;
      },
    },
  },
});
